import { useState } from "react";
import { X, Wrench, ArrowRight, Check, User, Car, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDay, fmtTime, STATUS_META } from "../lib";

/**
 * Convert an appointment → Job Card.
 * Displays a preview of what will be carried over and lets the advisor
 * override the assignee + promised delivery.
 */
export default function ConvertToJobCardDialog({ open, onClose, appointment, assignees = [], onConverted }) {
  const [assignedTo, setAssignedTo] = useState(appointment?.assignee_id || "");
  const [promisedAt, setPromisedAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [duplicate, setDuplicate] = useState(false);

  if (!open || !appointment) return null;

  const doConvert = async () => {
    setSaving(true);
    setError(null);
    try {
      const resp = await api.post(`/appointments/${appointment.id}/convert`, {
        assigned_to: assignedTo || null,
        promised_at: promisedAt || null,
        confirm_duplicate: duplicate,
      });
      toast.success(`Job card ${resp.data.job_number} created`);
      onConverted?.(resp.data);
      onClose?.();
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not convert";
      if (e.response?.status === 409 && /Already converted/i.test(detail)) {
        setDuplicate(true);
        setError(`${detail} — click Convert anyway to create a second job card.`);
      } else {
        setError(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="appt-convert-dialog-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        data-testid="appt-convert-dialog"
      >
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#1d3557]">
          <div className="flex items-center gap-2 text-white">
            <div className="w-9 h-9 rounded-lg bg-white/15 grid place-items-center">
              <Wrench size={17} />
            </div>
            <div>
              <div className="text-[15px] font-bold leading-tight">Convert to Job Card</div>
              <div className="text-[11px] text-white/70">
                <span className="font-mono">{appointment.number}</span> · will lock this appointment
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-md hover:bg-white/10 grid place-items-center text-white/80 hover:text-white"
            data-testid="appt-convert-close"
          >
            <X size={17} />
          </button>
        </header>

        <div className="p-6 space-y-4">
          {/* Carry-over preview */}
          <div className="rounded-xl border border-[#E4E7EC] bg-[#F7F9FC] p-3.5">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] mb-2">
              What carries over
            </div>
            <ul className="space-y-2 text-[13px]">
              <li className="flex items-center gap-2 text-[#1d3557]">
                <User size={13} className="text-[#457b9d]" />
                <span className="font-semibold">{appointment.customer_snapshot?.name || "—"}</span>
                <span className="text-[#667085]">{appointment.customer_snapshot?.phone}</span>
              </li>
              <li className="flex items-center gap-2 text-[#1d3557]">
                <Car size={13} className="text-[#457b9d]" />
                <span className="font-mono font-semibold">{appointment.vehicle_snapshot?.reg_number || "—"}</span>
                <span className="text-[#667085]">
                  {[appointment.vehicle_snapshot?.make, appointment.vehicle_snapshot?.model].filter(Boolean).join(" ")}
                </span>
              </li>
              <li className="flex items-center gap-2 text-[#1d3557]">
                <Calendar size={13} className="text-[#457b9d]" />
                <span>{fmtDay(appointment.scheduled_at)} · {fmtTime(appointment.scheduled_at)}</span>
              </li>
              {appointment.complaints && (
                <li className="pt-1 border-t border-[#E4E7EC]">
                  <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] mb-0.5">
                    Complaint
                  </div>
                  <div className="text-[12.5px] text-[#1d3557]">{appointment.complaints}</div>
                </li>
              )}
            </ul>
          </div>

          {/* Overrides */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Assign to</span>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                data-testid="appt-convert-assign"
                className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
              >
                <option value="">Unassigned</option>
                {assignees.map((a) => (
                  <option key={a.id} value={a.id}>
                    {(a.name || a.email)}{a.role ? ` · ${a.role}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Promised by</span>
              <input
                type="date"
                value={promisedAt}
                onChange={(e) => setPromisedAt(e.target.value)}
                data-testid="appt-convert-promised"
                className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
              />
            </label>
          </div>

          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]" data-testid="appt-convert-error">
              {error}
            </div>
          )}
        </div>

        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white"
            data-testid="appt-convert-cancel"
          >
            Cancel
          </button>
          <button
            onClick={doConvert}
            disabled={saving}
            className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="appt-convert-confirm"
          >
            {saving ? "Converting…" : (duplicate ? "Convert anyway" : (<><ArrowRight size={13} /> Create job card</>))}
          </button>
        </footer>
      </div>
    </div>
  );
}
